import React, { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Text, Textarea, useTheme } from '@Sage/hoi-poi-ui';

import FieldInsertButton from './FieldInsertButton';
import EmojiInsertButton from './EmojiInsertButton';
import { insertStringAtUtf16Index } from '../../utils/utf16SafeInsert';
import './styles.scss';

const normalizeTemplateToken = (value = '') => value.replace(/\{\{\s*([^}]+?)\s*\}\}/g, '{{$1}}');

const TextareaWithFieldInsert = memo(
    ({
        value,
        onChange,
        onFocus,
        onBlur,
        label,
        hint,
        isRequired,
        minRows,
        maxRows,
        isFullWidth,
        error,
        overrides,
        useCampaignReplaceVariables = false,
        formatInsertedVariable,
        inputRef,
    }) => {
        const theme = useTheme();
        const rootRef = useRef(null);
        const [cursorPosition, setCursorPosition] = useState(0);
        const errorMessage = typeof error === 'string' ? error : '';

        const getTextareaValue = useCallback(
            () => rootRef.current?.querySelector('textarea')?.value ?? value ?? '',
            [value],
        );

        useLayoutEffect(() => {
            if (!inputRef) {
                return;
            }
            inputRef.current = { getValue: getTextareaValue };
        }, [getTextareaValue, inputRef]);

        const handleClick = useCallback((e) => {
            setCursorPosition(e.target.selectionStart);
        }, []);

        const handleSelect = useCallback((e) => {
            setCursorPosition(e.target.selectionStart);
        }, []);

        const handleBlur = useCallback(
            (finalValue, e) => {
                if (e?.target) {
                    setCursorPosition(e.target.selectionStart);
                }
                onBlur?.(finalValue, e);
            },
            [onBlur],
        );

        const handleFocus = useCallback(
            (finalValue, e) => {
                if (e?.target) {
                    setCursorPosition(e.target.selectionStart);
                }
                onFocus?.(finalValue, e);
            },
            [onFocus],
        );

        const handleInsertField = useCallback(
            (option) => {
                const fieldValue = option?.value || '';
                const cleanValue = normalizeTemplateToken(fieldValue);
                const currentValue = getTextareaValue();
                const newValue = insertStringAtUtf16Index(currentValue, cursorPosition, cleanValue);

                onChange?.(newValue);
                setCursorPosition(cursorPosition + cleanValue.length);
            },
            [cursorPosition, getTextareaValue, onChange],
        );

        return (
            <div className="textarea-field-insert" ref={rootRef}>
                <div className="textarea-field-insert__input">
                    <Textarea
                        isFullWidth={isFullWidth}
                        isRequired={isRequired}
                        label={label}
                        hint={hint}
                        value={value}
                        onChange={onChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onClick={handleClick}
                        onSelect={handleSelect}
                        minRows={minRows}
                        maxRows={maxRows}
                        error={Boolean(error)}
                        overrides={overrides}
                    />
                    <div className="textarea-field-insert__toolbar">
                        <div className="textarea-field-insert__toolbar-left">
                            <EmojiInsertButton
                                onChange={onChange}
                                cursorPosition={cursorPosition}
                                value={value}
                                getValue={getTextareaValue}
                            />

                            <FieldInsertButton
                                onChange={handleInsertField}
                                useCampaignReplaceVariables={useCampaignReplaceVariables}
                                formatInsertedVariable={formatInsertedVariable}
                            />
                        </div>
                    </div>
                </div>
                {errorMessage && (
                    <Text
                        as="div"
                        type="caption"
                        color={theme.colors.semantic.negative500}
                        className="textarea-field-insert__error"
                    >
                        {errorMessage}
                    </Text>
                )}
            </div>
        );
    },
);

TextareaWithFieldInsert.displayName = 'TextareaWithFieldInsert';

TextareaWithFieldInsert.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    label: PropTypes.string,
    hint: PropTypes.string,
    isRequired: PropTypes.bool,
    minRows: PropTypes.number,
    maxRows: PropTypes.number,
    isFullWidth: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    overrides: PropTypes.object,
    useCampaignReplaceVariables: PropTypes.bool,
    formatInsertedVariable: PropTypes.func,
    inputRef: PropTypes.shape({ current: PropTypes.any }),
};

export default TextareaWithFieldInsert;
